import Routr from 'routr';
import Promise from 'bluebird';
import _ from 'lodash';
import Raven from './raven-config';
import api from './api';

import tournamentStatuses from './components/tournamentStatuses'

const debug = require('debug')('pointblank:routes');


function set404(data, errorMessage, errorMessageData){
  if(typeof Raven !== 'undefined'){
    Raven.captureMessage(errorMessage, {extra: errorMessageData});
  }
  data.setIn('route', {name: '404', params: {}, query: {}});
}


function route(path) {
  return function(target, key, descriptor) {
    if (!target.routesConfig) {
      target.routesConfig = {};
    }
    target.routesConfig[key] = {method: 'get', path};
  };
}

function headTitle(fn) {
  return function(target, key, descriptor) {
    if (!target.routesConfig) {
      target.routesConfig = {};
    }
    if (!target.routesConfig[key]) {
      target.routesConfig[key] = {};
    }
    target.routesConfig[key].title = function(...args) {
      return fn(...args);
    };
  };
}

export class Routes {

  constructor(api) {
    this.api = api;
    this.routr = new Routr(this.routesConfig);
  }

  getRoute(...args) {
    return this.routr.getRoute(...args);
  }

  makePath(...args) {
    return this.routr.makePath(...args);
  }

  @route('/')
  async home(data, params, query) {
    const requests = [];
    if (!data.getIn(['usersHomeTop'])) {
      requests.push(this.api.usersHomeTop(data, params, {sort: '-score'}));
    }
    if (!data.getIn(['teamsHomeTop'])) {
      // TODO data is on hidden tab, do not load in advance
      requests.push(this.api.teamsHomeTop(data, params, {sort: '-score'}));
    }
    if (!data.getIn(['news'])) {
      requests.push(this.api.news(data, params, query));
    }
    requests.push(this.loadRecentGames(data, params, query));
    // load tournaments if there were no tournaments loaded before
    // OR they were loaded with some not default query
    if (!data.getIn(['tournaments']) || !_.isEmpty(data.getIn(['tournaments', 'query']).toJS())) {
      requests.push(this.api.tournaments(data, params, query));
    }
    await* requests;
  }

  @route('/news')
  async newsHome(data, params, query) {
    await this.api.news(data, params, query);
  }

  @route('/news/:id/:slug')
  @headTitle((navTitle, {newsTitle}) => `${newsTitle}`)
  async newsDetails(data, {id}, query) {
    const entityID = parseInt(id);
    if (!data.hasData(['newsData', entityID])) {
      const response = await this.api.newsRead(entityID);
      data.setIn(['newsData', entityID], response.data.item);
      data.setIn(['newsData', entityID, 'randomNews'], response.data.random);
      data.setIn(['title', 'params', 'newsTitle'], response.data.item.title);
    }
  }

  @route('/tournaments')
  @headTitle((navTitle, {}, {}, {type}) => type ? `${navTitle} турниры` : `Все турниры`)
  async tournaments(data, params, query) {
    let requests = [];
    if(query.type === 'in_progress'){
      requests.push(this.api.tournaments(data, params, {...query, type: 'pending'}));
      requests.push(this.api.tournaments(data, params, {...query, type: 'registration'}));
      requests.push(this.api.tournaments(data, params, {...query, type: 'confirmation'}));
      requests.push(this.api.tournaments(data, params, {...query, type: 'in_progress'}));
    }else{
      requests.push(this.api.tournaments(data, params, query));
    }
    requests.push(this.loadRecentGames(data, params, query));
    await* requests;
  }

  async loadRecentGames(data, params, query) {
    if (data.getIn(['gamesRecent'])) {
    } else {
      const response = await this.api.gamesRecent(data, params, query);
      data.cursor(['gamesRecent']).set(response.data);
    }
  }

  async loadTournamentData(data, params, query) {
    let tournamentId = parseInt(params.id);
    let stageId = params.stageId && parseInt(params.stageId);

    if (!data.getIn(['tournamentData', tournamentId])) {
      let response = await this.api.tournamentsRead(tournamentId);
      data.cursor(['tournamentData', tournamentId]).set(response.data);
    }

    if (stageId){
      let tournament = data.getIn(['tournamentData', tournamentId]);
      if(tournament && !_.find(tournament.toJS().stages, {id: stageId})){
        set404(data, 'stage not found in tournament', {where: 'client/route.js', stageId: stageId, tournamentId: tournamentId});
      }

      if(!data.getIn(['stages', stageId])) {
        let response = await this.api.stagesRead(stageId);
        data.cursor(['stages', stageId]).set(response.data);
      }
      data.setIn(['title', 'params', 'tournamentTitle'],
        data.getIn(['tournamentData', tournamentId, 'title'])+
        ' / '+
        data.getIn(['stages', stageId, 'title'])
      );
    }else{
      data.setIn(['title', 'params', 'tournamentTitle'],
        data.getIn(['tournamentData', tournamentId, 'title'])
      );
    }
  }

  async loadTeamData(data, params, query) {
    const userTeamId = data.cursor('team').getIn('id');
    if (!data.getIn(['teamData', userTeamId])) {
      if (data.cursor('user').get('has_team')) {
        let response = await this.api.teamRead(userTeamId);
        data.setIn(['title', 'params', 'teamTitle'], response.data.title);
        data.cursor(['teamData', userTeamId]).set(response.data);
      }
    }
  }

  @route('/tournaments/:id')
  @headTitle((navTitle, {tournamentTitle}, {id, stageId}) => `${tournamentTitle}`)
  async tournamentDetails(data, params, query) {
    await* [
      this.loadTeamData(data, params, query),
      this.loadTournamentData(data, params, query),
      this.loadRecentGames(data, params, query)
    ];
    const tournament = data.getIn(['tournamentData', parseInt(params.id)]);
    if(tournament.get('status') < tournamentStatuses.in_progress.id || !tournament.get('stages').size){
      data.setIn('redirectTo', {name: 'tournamentTeamsList', params: params});
    }else{
      const stages = tournament.get('stages').toJS();
      let currentStage = _.find(stages, {is_complete: false});
      if(!currentStage){
        currentStage = stages[stages.length-1];
      }
      data.setIn('redirectTo', {name: 'tournamentStage', params: {id: params.id, stageId: currentStage.id}});
    }
  }

  @route('/tournaments/:id/rules')
  @headTitle((navTitle, {tournamentTitle}, {id, stageId}) => `${tournamentTitle} / Правила`)
  async tournamentRules(data, params, query) {
    await* [
      this.loadTeamData(data, params, query),
      this.loadTournamentData(data, params, query),
      this.loadRecentGames(data, params, query)
    ];
  }

  @route('/tournaments/:id/teams')
  @headTitle((navTitle, {tournamentTitle}, {id, stageId}) => `${tournamentTitle} / Участники`)
  async tournamentTeamsList(data, params, query) {
    await* [
      this.loadTeamData(data, params, query),
      this.loadTournamentData(data, params, query),
      this.loadRecentGames(data, params, query)
    ];
  }

  @route('/tournaments/:id/stage/:stageId')
  @headTitle((navTitle, {tournamentTitle}, {id, stageId}) => `${tournamentTitle}`)
  async tournamentStage(data, params, query) {
    await* [
      this.loadTeamData(data, params, query),
      this.loadTournamentData(data, params, query),
      this.loadRecentGames(data, params, query)
    ];
  }

  @route('/tournaments/:id/stage/:stageId/:modificator')
  @headTitle((navTitle, {tournamentTitle}, {id, stageId}) => `${tournamentTitle}`)
  async tournamentStageInner(data, params, query) {
    await* [
      this.loadTeamData(data, params, query),
      this.loadTournamentData(data, params, query),
      this.loadRecentGames(data, params, query)
    ];
  }

  @route('/teams')
  @headTitle((navTitle, {}, {}, {q, sort}) => {
    return (q || sort)
        ? `Команды по ${navTitle.toLowerCase()}`
        : navTitle;
  })
  async teams(data, params, query) {
    await this.api.teams(data, params, query);
  }

  @route('/teams/new')
  @headTitle(() => `Создание команды`)
  async teamsNew() {

  }

  @route('/teams/:id')
  @headTitle((navTitle, {teamTitle}, params, query) => `${teamTitle}`)
  async teamDetails(data, {id}, query) {
    let entityID = parseInt(id);
    if (data.getIn(['teamData', entityID])) {
    } else if (_.isFinite(entityID)) {
      let response = await* [
        this.api.teamRead(entityID),
        this.api.teamGames(data, {id}, query),
        this.api.teamTournaments(data, {id}, query)
      ];
      response = response[0];
      var teamData = data.cursor(['teamData', entityID]);
      data.setIn(['title', 'params', 'teamTitle'], response.data.title);
      teamData.set(response.data);
    }
  }

  @route('/players')
  @headTitle((navTitle, {}, {}, {q, sort}) => {
    return (q || sort)
        ? `Игроки${sort === 'has_team' ? '' : ' по'} ${navTitle.toLowerCase()}`
        : navTitle;
  })
  async players(data, params, query) {
    await this.api.users(data, params, query);
  }

  @route('/players/:id')
  @headTitle((navTitle, {playerNickname}, params, query) => `${playerNickname}`)
  async playerDetails(data, params, query) {
    let entityID = parseInt(params.id);
    if (data.getIn(['userData', entityID])) {
    } else {
      let response = await* [
        this.api.userRead(entityID),
        this.api.userGames(data, params, query),
        this.api.userTournaments(data, params, query)
      ];
      response = response[0];
      var userData = data.cursor(['userData', entityID]);
      data.setIn(['title', 'params', 'playerNickname'], response.data.nickname);
      userData.set(response.data);
    }
  }

  @route('/games/:id')
  async gameDetails(data, {id}) {
    const entityID = parseInt(id);
    const cursor = data.cursor(['gameData', entityID]);
    if (!cursor.deref()) {
      const response = await this.api.gameRead(entityID);
      cursor.set(response.data);
    }
  }

  @route('/faq')
  async faq(data) {
    await this.api.faq(data);
  }

  @route('/sitemap')
  async sitemap() {

  }

  @route('/feedback')
  async feedback() {

  }

  @route('/404')
  async '404'() {

  }

  @route('/500')
  async '500'() {

  }

  @route('/login')
  async signin() {
  }

  @route('/playground')
  async playground() {

  }

  @route('/flatpages/:id')
  async flatPage(data, {id}) {
    const pageId = parseInt(id);
    if (!data.getIn(['flatPagesData', pageId])) {
      let response = await this.api.flatpage(pageId);
      data.setIn(['flatPagesData', pageId], response.data);
      data.setIn(['title', 'params', 'flatPageTitle'], response.data.title);
    }
  }

}


export default new Routes(api);



// WEBPACK FOOTER //
// ./src/routes.js